import Alert from '@jg/alert';
import Loading from '@jg/loading';
import cn from 'classnames';
import i18next from 'i18next';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Result from '../components/Result';
import campaignIcon from '../icons/campaignIcon.svg';
import charityIcon from '../icons/charityIcon.svg';
import crowdfundingIcon from '../icons/crowdfundingIcon.svg';
import fundraisingIcon from '../icons/fundraisingIcon.svg';
import teamIcon from '../icons/teamIcon.svg';
import * as dto from '../lib/dto';
import { pageSectionFromGroupedResults, TrackFunc } from '../lib/tracking';
import Grid from './Grid';
import GroupTitle from './GroupTitle';
import Reveal from './Reveal';

interface Props {
  groupedResults: dto.GroupedResults;
  loadMore: (args: {
    index: dto.GroupedResults['Specific'];
    offset: number;
  }) => void;
  paginationState: 'idle' | 'loading' | 'error';
  track: TrackFunc;
}

export default React.memo(function GroupedResults({
  groupedResults,
  loadMore,
  paginationState,
  track,
}: Props) {
  const results: dto.Result[] = groupedResults.Results;

  const { t } = useTranslation();

  const loadMoreLabel = t('loadMore', 'Load more');

  return (
    <>
      <GroupTitle
        title={title(t, groupedResults)}
        icon={icon(groupedResults)}
      />

      <Grid
        testId={`jg-search-results--${groupedResults.Specific}`}
        smCols={groupedResults.Specific === 'charity' ? 1 : 2}
        lgCols={groupedResults.Specific === 'charity' ? 2 : 3}
      >
        {results.map(result => (
          <Result key={result.Link} result={result} track={track} />
        ))}
      </Grid>

      {groupedResults.Count > groupedResults.Results.length && (
        <>
          <div className="row">
            <div
              className={cn(
                'col-md-6 offset-md-3 jg-space-phsm',
                'col-lg-4 offset-lg-4'
              )}
            >
              <button
                data-testid={`jg-search-load-next-page--${
                  groupedResults.Specific
                }`}
                className={cn(
                  'jg-btn jg-btn--ghost jg-space-mvxs',
                  {
                    'jg-space-mtms': groupedResults.Specific === 'charity',
                  },
                  'jg-space-mtsm@md jg-space-mbn@md'
                )}
                onClick={() => {
                  track({
                    event: 'click',
                    subtype: 'button',
                    event_value: 'load more',
                    page_section: pageSectionFromGroupedResults(groupedResults),
                    action_type: 'number of clicks',
                    action_id: groupedResults.PageNumber,
                  });
                  loadMore({
                    index: groupedResults.Specific,
                    offset: groupedResults.Results.length,
                  });
                }}
              >
                {paginationState === 'loading' ? (
                  <Reveal afterMs={500} untilThenRender={<>{loadMoreLabel}</>}>
                    <Loading size="small" />
                  </Reveal>
                ) : (
                  loadMoreLabel
                )}
              </button>
            </div>
          </div>

          {paginationState === 'error' && (
            <div
              className={cn(
                'jg-space-mha jg-space-mtsm jg-space-mbxs',
                'jg-space-mtms@md'
              )}
            >
              <Alert
                type="error"
                displayType="inline"
                onClose={false as any} // @jg/alert bug
              >
                <Trans i18nKey="loadMore.error">
                  We couldn’t fetch your search results right now, please try
                  again
                </Trans>
              </Alert>
            </div>
          )}
        </>
      )}
    </>
  );
});

function title(
  t: i18next.TFunction,
  groupedResults: dto.GroupedResults
): string {
  switch (groupedResults.Specific) {
    case 'campaign':
      return t('resultGroup.campaign.title', 'Campaigns');
    case 'charity':
      return t('resultGroup.charity.title', 'Charities');
    case 'fundraiser':
      return t('resultGroup.fundraiser.title', 'Fundraisers');
    case 'localproject':
      return t('resultGroup.localproject.title', 'Crowdfunders');
    case 'teamsv1':
      return t('resultGroup.teamsv1.title', 'Teams');
    case 'page':
      return t('resultGroup.page.title', 'Birthdays');
  }
}

function icon(groupedResults: dto.GroupedResults): string {
  switch (groupedResults.Specific) {
    case 'campaign':
      return campaignIcon;
    case 'charity':
      return charityIcon;
    case 'fundraiser':
      return fundraisingIcon;
    case 'localproject':
      return crowdfundingIcon;
    case 'teamsv1':
      return teamIcon;
    case 'page':
      return fundraisingIcon; //TODO: birthdayicon
  }
}
